import React from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useTranslation } from 'react-i18next';
import UserAddressService from '../../../services/useraddress/useraddress.service';

interface AddCountryProps {
    showCountryDialog: boolean;
    hideCountryDialog: () => void;
    handleAddSuccess: () => void;
}

const AddCountry: React.FC<AddCountryProps> = ({ 
    showCountryDialog, 
    hideCountryDialog,
    handleAddSuccess 
}) => {
    const { t } = useTranslation();
    const [countryName, setCountryName] = React.useState('');
    const [countryCode, setCountryCode] = React.useState('');
    const [nameError, setNameError] = React.useState('');
    const [codeError,setCodeError]= React.useState('');
    const toast = React.useRef<Toast>(null);

    const showMessage = (
        severity: 'success' | 'error',
        summary: string,
        detail: string
    ) => {
        toast.current?.show({
            severity,
            summary,
            detail,
            life: 3000
        });
    };

    const saveCountry = async () => {
        if (!countryName.trim()) {
            setNameError('Country name is required');
            return;
        }
        if (!countryCode.trim()) {
            setCodeError('Country code is required');
            return;
        }

        try {
            const result = await UserAddressService.addNewCountry({
                countryName: countryName, 
                countryCode: countryCode
            });

            if (result.isSuccess) {
                handleAddSuccess(); 
                hideCountryDialog();
                setCountryName('');
                setCountryCode('');
            } else {
                showMessage('error', 'info', result.message || 'CountryName or CountryCode already exists');
            }
        } catch (error) {
            showMessage('error', 'Error', 'An unexpected error occurred');
            console.error('Error saving country:', error);
        }
    };

    const handleCloseModal = () => {
        hideCountryDialog();
        setCountryName('');
        setCountryCode('');
        setNameError('');
    };

    return (
        <Dialog
            visible={showCountryDialog}
            className="quickview w-50"
            breakpoints={{ '960px': '75vw', '641px': '90vw' }}
            modal
            onHide={handleCloseModal}
            closable={false}
            header={() => (
                <div>
                <div className="flex justify-content-end p-2">
                    <Button 
                        icon="pi pi-times" 
                        onClick={handleCloseModal}
                        className="p-button-text p-button-rounded"
                        aria-label="Close"
                    />
                </div>
                <div className="bg-green-700 rounded-t text-center p-2">
  <span className="text-white font-bold">Add New Country</span>
</div>

                
            </div>
            )}
        >
            <div className="flex flex-wrap">
                <div className="w-full mb-3 px-3">
                    <div className="field">
                        <label htmlFor="countryName" className="label-semi-bold">
                            Country Name<span className="required">*</span>
                        </label>
                        <InputText
                            id="countryName"
                            placeholder="Country Name"
                            style={{ width: '100%' }}
                            value={countryName}
                            onChange={(e) => {
                                setCountryName(e.target.value);
                                if (e.target.value.trim()) setNameError('');
                            }}
                            onKeyDown={(e) => {
                                if (!/^[a-zA-Z ]$/.test(e.key) && e.key !== 'Backspace') {
                                    e.preventDefault();
                                }
                            }}
                        />
                        {nameError && <small className="p-error">{nameError}</small>}
                    </div>
                </div>
                <div className="w-full mb-3 px-3">
                    <div className="field">
                        <label htmlFor="countryCode" className="label-semi-bold">
                            Country Code<span className="required">*</span>
                        </label>
                        <InputText
                            id="countryCode"
                            placeholder="Country Code"
                            style={{ width: '100%' }}
                            value={countryCode}
                            onChange={(e) => {
                                setCountryCode(e.target.value);
                                if (e.target.value.trim()) setCodeError('');
                            }}
                            onKeyDown={(e) => {
                                if (!/^[a-zA-Z ]$/.test(e.key) && e.key !== 'Backspace') {
                                    e.preventDefault();
                                }
                            }}
                        />
                         {codeError && <small className="p-error">{codeError}</small>}
                    </div>
                </div>
            </div>
            <div className="text-right mt-4">
                <Button
                    label="Save"
                    onClick={saveCountry}
                    className="bg-green-700 text-white"
                />
            </div>
            <Toast ref={toast} /> 
        </Dialog>
    );
};

export default AddCountry;